import React, { useMemo, useCallback } from 'react';
import { Typography, Button, Upload, Flex, Spin } from 'antd';
import type { UploadFile, UploadProps } from 'antd';
import useFilesize from 'hooks/useFilesize';
import useFiletype from 'hooks/useFiletype';
import { useDateFormat } from 'hooks/useDateFormat';
import { useUploadAttachmentProps } from 'hooks/attachments.hooks';
import { useTranslation } from 'react-i18next';
import { UploadOutlined } from '@ant-design/icons';

const { Dragger } = Upload;

function MultiUpload({
  attachments,
  action,
  onFileListChanged,
}: {
  attachments: UploadFile[];
  action: string;
  onFileListChanged?: () => void;
}) {
  const filesize = useFilesize();
  const filetype = useFiletype();
  const format = useDateFormat();
  const { t } = useTranslation();
  const uploadProps: UploadProps = useUploadAttachmentProps(
    attachments,
    action,
    onFileListChanged,
  );

  const extra = useCallback(
    ({ size = 0, lastModifiedDate = new Date(), name }: UploadFile) => (
      <>
        <Typography.Text className="float-right">
          {format(lastModifiedDate, 'PP')} &ndash; {filesize(size)} &ndash;{' '}
          {filetype(name)}
        </Typography.Text>
      </>
    ),
    [filesize, filetype, format],
  );

  const showUploadList = useMemo(
    () => ({
      extra,
      showDownloadIcon: false,
      showRemoveIcon: false,
    }),
    [extra],
  );

  return (
    <Dragger {...uploadProps} showUploadList={showUploadList} multiple>
      <Flex gap="large">
        <Button type="primary" icon={<UploadOutlined />}>
          {t('attachments:ajouter_fichier')}
        </Button>
        <p className="ant-upload-text grow !-mb-1 pt-1">
          {t('attachments:deposez_fichiers')}
        </p>
      </Flex>
    </Dragger>
  );
}

export default MultiUpload;
